import React, { useState } from 'react';

import { ActionIcon, Button, Flex, ScrollArea, Stack, TextInput, Textarea, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconClipboardCheck, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DroxyModal } from 'components/Droxy/DroxyModal';
import { useTranslation } from 'react-i18next';
import { EvaluationService } from 'services/evaluation-service';
import { NotificationService } from 'services/notification-service';
import {
  MAX_EVALUATION_NAME_LENGTH,
  MAX_SINGLE_STEP_QUESTIONS,
  MAX_SINGLE_STEP_QUESTION_LENGTH,
  MIN_SINGLE_STEP_QUESTIONS
} from 'shared/constants/evaluation';
import { EVALUATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import { Evaluation } from 'shared/interfaces/Evaluation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { maxLengthValidator, requiredInputValidator, uniqueArrayValidator } from 'shared/utils/form-validators';

interface UpdateEvaluationModalProps {
  opened: boolean;
  onClose: () => void;
  smartAssistant: SmartAssistant;
  evaluation: Evaluation;
}

export function ResetEvaluationModal({ opened, onClose, smartAssistant, evaluation }: UpdateEvaluationModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);

  const resetEvaluationMutation = useMutation({
    mutationFn: async () => {
      setIsSaving(true);
      await EvaluationService.resetEvaluation(evaluation._id, {
        smartAssistantId: smartAssistant._id,
        name: resetEvaluationForm.values.name,
        singleStepQuestions: resetEvaluationForm.values.singleStepQuestions.map((question) => question.question)
      });
    },
    onSuccess: () => {
      NotificationService.showResult(true, t('FEED_BACK.EVALUATION.SUCCESS.UPDATE'));
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_QUERY_KEY, smartAssistant._id] });
      onClose();
    },
    onError: () => {
      NotificationService.showResult(false, t('FEED_BACK.EVALUATION.FAILURE.UPDATE'));
    },
    onSettled: () => {
      setIsSaving(false);
    }
  });

  const resetEvaluationForm = useForm({
    initialValues: {
      name: evaluation.name,
      singleStepQuestions: evaluation.evaluationTests.map((test) => ({
        question: test.input.singleStepTestInput.content
      })) || [{ question: '' }]
    },
    validate: {
      name: (value) => requiredInputValidator(value) || maxLengthValidator(value, MAX_EVALUATION_NAME_LENGTH),
      singleStepQuestions: {
        question: (value, values) =>
          requiredInputValidator(value) ||
          maxLengthValidator(value, MAX_SINGLE_STEP_QUESTION_LENGTH) ||
          uniqueArrayValidator(
            values.singleStepQuestions.map((question) => question.question),
            value
          )
      }
    }
  });

  const addQuestion = () => {
    resetEvaluationForm.setFieldValue('singleStepQuestions', [
      ...resetEvaluationForm.values.singleStepQuestions,
      { question: '' }
    ]);
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = [...resetEvaluationForm.values.singleStepQuestions];
    updatedQuestions.splice(index, 1);
    resetEvaluationForm.setFieldValue('singleStepQuestions', updatedQuestions);
    resetEvaluationForm.setErrors({});
  };

  const handleSaveEvaluation = () => {
    const validation = resetEvaluationForm.validate();
    if (validation.hasErrors) return;

    resetEvaluationMutation.mutate();
  };

  return (
    <DroxyModal
      titleIcon={<IconClipboardCheck />}
      size="xl"
      title={t('SMART_ASSISTANT.EVALUATIONS_SECTION.UPDATE_EVALUATION')}
      description={t('SMART_ASSISTANT.EVALUATIONS_SECTION.UPDATE_EVALUATION_DESCRIPTION')}
      opened={opened}
      onClose={onClose}>
      <Stack spacing="md">
        <Stack spacing={4}>
          <Title order={4}>{t('SMART_ASSISTANT.EVALUATIONS_SECTION.NAME')}</Title>
          <TextInput
            placeholder={t('SMART_ASSISTANT.EVALUATIONS_SECTION.NAME_PLACEHOLDER')}
            {...resetEvaluationForm.getInputProps('name')}
          />
        </Stack>

        <Stack spacing={4}>
          <Title order={4}>{t('SMART_ASSISTANT.EVALUATIONS_SECTION.QUESTIONS_TITLE')}</Title>
          <ScrollArea.Autosize mah={320} scrollbarSize={0}>
            <Stack spacing={8}>
              {resetEvaluationForm.values.singleStepQuestions.map((question, index) => (
                <Flex key={index} gap={8} align="center">
                  <Textarea
                    style={{ flex: 1 }}
                    styles={{ input: { overflow: 'hidden' } }}
                    minRows={2}
                    maxRows={2}
                    placeholder={t('SMART_ASSISTANT.EVALUATIONS_SECTION.QUESTION_PLACEHOLDER')}
                    {...resetEvaluationForm.getInputProps(`singleStepQuestions.${index}.question`)}
                  />
                  <ActionIcon
                    disabled={resetEvaluationForm.values.singleStepQuestions.length <= MIN_SINGLE_STEP_QUESTIONS}
                    onClick={() => removeQuestion(index)}>
                    <IconTrash />
                  </ActionIcon>
                </Flex>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>

        <Button
          disabled={resetEvaluationForm.values.singleStepQuestions.length >= MAX_SINGLE_STEP_QUESTIONS}
          leftIcon={<IconPlus />}
          variant="default"
          onClick={addQuestion}
          style={{ alignSelf: 'flex-start' }}>
          {t('SMART_ASSISTANT.EVALUATIONS_SECTION.ADD_QUESTION')}
        </Button>

        <Flex gap={16} justify="flex-end" mt={24}>
          <Button onClick={handleSaveEvaluation} loading={isSaving}>
            {t('SMART_ASSISTANT.EVALUATIONS_SECTION.SAVE_EVALUATION')}
          </Button>
        </Flex>
      </Stack>
    </DroxyModal>
  );
}
